/**
 * URL-backed query state, data loading and RBAC tables for the event screens.
 *
 * Mirrors `pages/roster/use-roster.ts`: the whole list query (search, season,
 * type, status, sort, page, page size) lives in the address bar, so a refresh
 * or a shared link restores the same view.
 *
 * Three hooks, one per resource the events slice reads:
 *   `useEventList()`          → `GET /events/v1`
 *   `useEventDetail(id)`      → `GET /events/v1/{id}`
 *   `useParticipation(id)`    → `GET /events/v1/{id}/participation`
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { ApiError } from '../../lib/api-error';
import type { EventPage, ParticipationPage, TeamEvent } from '../../lib/api-types';
import { PAGE_SIZE_OPTIONS } from '../../lib/constants';
import type { Persona } from '../../lib/personas';
import {
  enumParam,
  intChoiceParam,
  intParam,
  readUrlState,
  textParam,
  writeUrlState,
  URL_PARAM_KEYS,
} from '../../lib/url-state';

/** Whitelisted `sort_by` values — must match `EVENT_SORT_FIELDS` in services/events. */
export const EVENT_SORT_FIELDS = [
  'scheduled_at',
  'name',
  'event_type',
  'status',
  'season_year',
  'created_at',
] as const;
export type EventSortField = (typeof EVENT_SORT_FIELDS)[number];

/** Upcoming/most recent first is what a coach wants on landing (FR-EVENTS-02). */
export const EVENT_DEFAULT_SORT: EventSortField = 'scheduled_at';
export const EVENT_DEFAULT_SORT_ORDER = 'desc';
export const EVENTS_DEFAULT_PAGE_SIZE = 25;

/**
 * RBAC affordance tables, mirroring `services/events/src/constants.ts`. Hiding a
 * control the server would refuse keeps the UI honest; it is not the boundary.
 */
export const EVENT_WRITE_PERSONAS: readonly Persona[] = [
  'platform_admin',
  'team_admin',
  'offensive_coordinator',
  'defensive_coordinator',
  'coach',
];
/** Close: Team Admin + OC/DC/Coach. Reopen: administrators only (FR-EVENTS-03). */
export const EVENT_CLOSE_PERSONAS: readonly Persona[] = EVENT_WRITE_PERSONAS;
export const EVENT_REOPEN_PERSONAS: readonly Persona[] = ['platform_admin', 'team_admin'];
/** `DELETE /events/v1/{event_id}` — Team Admin only (coaches and operators get 403). */
export const EVENT_DELETE_PERSONAS: readonly Persona[] = ['platform_admin', 'team_admin'];
/** Participation writes match event writes; stat operators read only (FR-EVENTS-05). */
export const PARTICIPATION_WRITE_PERSONAS: readonly Persona[] = EVENT_WRITE_PERSONAS;

export function canPersona(persona: Persona | null, allowed: readonly Persona[]): boolean {
  return persona !== null && allowed.includes(persona);
}

export interface EventQueryState {
  search: string;
  /** Season year as a string, or `all`. */
  season: string;
  /** Event type, or `all`. */
  type: string;
  /** Status, or `all`. */
  status: string;
  sortBy: EventSortField;
  sortOrder: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

/** Declarative screen state (shared url-state kit): parse is total, defaults stay off the URL. */
const EVENTS_URL_STATE = {
  search: textParam(URL_PARAM_KEYS.search),
  season: textParam(URL_PARAM_KEYS.season, 'all'),
  type: textParam('type', 'all'),
  status: textParam('status', 'all'),
  sortBy: enumParam(URL_PARAM_KEYS.sortBy, EVENT_SORT_FIELDS, EVENT_DEFAULT_SORT),
  sortOrder: enumParam(
    URL_PARAM_KEYS.sortOrder,
    ['asc', 'desc'] as const,
    EVENT_DEFAULT_SORT_ORDER
  ),
  page: intParam(URL_PARAM_KEYS.page, { fallback: 1, min: 1 }),
  pageSize: intChoiceParam(URL_PARAM_KEYS.pageSize, PAGE_SIZE_OPTIONS, EVENTS_DEFAULT_PAGE_SIZE),
};

function readQuery(params: URLSearchParams): EventQueryState {
  return readUrlState(EVENTS_URL_STATE, params);
}

function writeQuery(next: EventQueryState, base: URLSearchParams): URLSearchParams {
  return writeUrlState(EVENTS_URL_STATE, next, base);
}

export interface EventListController {
  query: EventQueryState;
  /** Merge a partial query. Anything but an explicit page change resets to page 1. */
  setQuery: (patch: Partial<EventQueryState>) => void;
  /** True when nothing is filtered — separates first-run from no-matches. */
  isUnfiltered: boolean;
  events: readonly TeamEvent[];
  total: number;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

const LIST_ERROR = 'The event list could not be loaded. Please try again.';

export function useEventList(): EventListController {
  const apiClient = useApiClient();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState<EventPage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const query = useMemo(() => readQuery(params), [params]);

  const setQuery = useCallback(
    (patch: Partial<EventQueryState>) => {
      const next = { ...readQuery(params), ...patch };
      if (patch.page === undefined) next.page = 1;
      setParams(writeQuery(next, params), { replace: true });
    },
    [params, setParams]
  );

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    apiClient
      .listEvents(
        {
          q: query.search === '' ? undefined : query.search,
          season: query.season === 'all' ? undefined : Number(query.season),
          type: query.type === 'all' ? undefined : query.type,
          status: query.status === 'all' ? undefined : query.status,
          sort_by: query.sortBy,
          sort_order: query.sortOrder,
          page: query.page,
          limit: query.pageSize,
        },
        controller.signal
      )
      .then((result) => {
        if (!controller.signal.aborted) setPage(result ?? null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(ApiError.isApiError(caught) ? caught.message : LIST_ERROR);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [apiClient, query, reloadToken]);

  return {
    query,
    setQuery,
    isUnfiltered:
      query.search === '' &&
      query.season === 'all' &&
      query.type === 'all' &&
      query.status === 'all',
    events: page?.items ?? [],
    total: page?.total ?? 0,
    isLoading,
    error,
    reload,
  };
}

export interface EventDetailController {
  event: TeamEvent | null;
  isLoading: boolean;
  error: string | null;
  /** True when the server said 404/403 — the page renders a dead-end message. */
  isMissing: boolean;
  reload: () => void;
  /** Adopt a row returned by a mutation without a second round trip. */
  apply: (event: TeamEvent) => void;
}

export function useEventDetail(eventId: string | undefined): EventDetailController {
  const apiClient = useApiClient();
  const [event, setEvent] = useState<TeamEvent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMissing, setIsMissing] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);
  const apply = useCallback((next: TeamEvent) => setEvent(next), []);

  useEffect(() => {
    if (eventId === undefined || eventId === '') {
      setIsLoading(false);
      setIsMissing(true);
      return undefined;
    }
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    setIsMissing(false);

    apiClient
      .getEvent(eventId, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setEvent(result ?? null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        const isApi = ApiError.isApiError(caught);
        if (isApi && (caught.status === 404 || caught.status === 403)) {
          setIsMissing(true);
          setError(
            caught.status === 403
              ? 'You do not have access to this event.'
              : 'This event no longer exists.'
          );
          return;
        }
        setError(isApi ? caught.message : 'This event could not be loaded. Please try again.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [apiClient, eventId, reloadToken]);

  return { event, isLoading, error, isMissing, reload, apply };
}

export interface ParticipationController {
  page: ParticipationPage | null;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
  /** Adopt the envelope a PUT returned. */
  apply: (page: ParticipationPage) => void;
}

export function useParticipation(eventId: string | undefined): ParticipationController {
  const apiClient = useApiClient();
  const [page, setPage] = useState<ParticipationPage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);
  const apply = useCallback((next: ParticipationPage) => setPage(next), []);

  useEffect(() => {
    if (eventId === undefined || eventId === '') {
      setIsLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    apiClient
      .listParticipation(eventId, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setPage(result ?? null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          ApiError.isApiError(caught)
            ? caught.message
            : 'Participation could not be loaded. Please try again.'
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [apiClient, eventId, reloadToken]);

  return { page, isLoading, error, reload, apply };
}
